import CONFIG from "@/config/config";
import { getToken } from "../../axios";

export interface StartLiveTalkdownResult {
  data?: any;
  message?: string;
  success: boolean;
}

const parseTalkdownResponse = async (
  response: Response,
  fallbackMessage: string
): Promise<StartLiveTalkdownResult> => {
  const body = await response.json().catch(() => ({}));

  if (!response.ok || body?.success === false) {
    return {
      message: body?.message || fallbackMessage,
      success: false,
    };
  }

  return {
    data: body?.data?.data ?? body?.data,
    success: true,
  };
};

export const startLiveTalkdownSession = async (
  deviceId: string
): Promise<StartLiveTalkdownResult> => {
  try {
    const token = getToken();
    const response = await fetch(
      `${CONFIG.api}/milestone/talkdown/live/session?deviceId=${encodeURIComponent(deviceId)}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );

    return parseTalkdownResponse(response, "Failed to start live talkdown.");
  } catch (error: any) {
    return {
      message: error?.message || "Failed to start live talkdown.",
      success: false,
    };
  }
};

export const sendLiveTalkdownChunk = async (
  sessionId: string,
  audioChunk: Uint8Array
): Promise<StartLiveTalkdownResult> => {
  try {
    const token = getToken();
    const response = await fetch(
      `${CONFIG.api}/milestone/talkdown/live/session/${encodeURIComponent(sessionId)}/chunk`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/octet-stream",
          Accept: "application/json",
        },
        body: audioChunk,
      }
    );

    return parseTalkdownResponse(response, "Failed to send live talkdown audio.");
  } catch (error: any) {
    return {
      message: error?.message || "Failed to send live talkdown audio.",
      success: false,
    };
  }
};

export const stopLiveTalkdownSession = async (
  sessionId: string
): Promise<StartLiveTalkdownResult> => {
  try {
    const token = getToken();
    const response = await fetch(
      `${CONFIG.api}/milestone/talkdown/live/session/${encodeURIComponent(sessionId)}/stop`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );

    return parseTalkdownResponse(response, "Failed to stop live talkdown.");
  } catch (error: any) {
    return {
      message: error?.message || "Failed to stop live talkdown.",
      success: false,
    };
  }
};

const startLiveTalkdown = async (
  deviceId: string,
  audioStream: ReadableStream<Uint8Array>
): Promise<StartLiveTalkdownResult> => {
  try {
    const token = getToken();
    const response = await fetch(
      `${CONFIG.api}/milestone/talkdown/live?deviceId=${encodeURIComponent(deviceId)}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/octet-stream",
        },
        body: audioStream as unknown as BodyInit,
        duplex: "half",
      } as RequestInit & { duplex: "half" }
    );

    const body = await response.json().catch(() => ({}));

    if (!response.ok || body?.success === false) {
      return {
        message: body?.message || "Live talkdown failed.",
        success: false,
      };
    }

    return {
      data: body?.data,
      success: true,
    };
  } catch (error: any) {
    return {
      message: error?.message || "Live talkdown failed.",
      success: false,
    };
  }
};

export default startLiveTalkdown;
